import type { UseGetOptions } from "./useGet.type";
import type { ApiError, ApiPayloadGet } from "@/services/api";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "@/config/client";
import { QUERY_PERSIST_KEY } from "@/services/query";

export const useGet = <Data = unknown, Meta = unknown>(
  model: string,
  id?: string | number | null,
  options?: UseGetOptions<Data, Meta>
) => {
  const queryClient = useQueryClient();
  const cleanId = typeof id === "number" ? id : parseInt(id ?? "");

  const {
    params = {},
    rootId = null,
    endPoint = "get",
    neverStale = false,
    enabled = true,
    persist = false,
    queryKeyExtraParams = [],
    ...otherOptions
  } = options ?? {};

  const persistKey = persist ? [QUERY_PERSIST_KEY] : [];
  const queryKey = [
    model,
    endPoint,
    cleanId,
    rootId,
    params,
    ...queryKeyExtraParams,
    ...persistKey,
  ].filter(Boolean);

  const idIsInvalid = !id || isNaN(cleanId);

  const { data, ...queryResult } = useQuery<
    ApiPayloadGet<Data, Meta>,
    ApiError
  >({
    staleTime: neverStale ? Infinity : 0,
    refetchOnWindowFocus: false,
    ...otherOptions,
    enabled: idIsInvalid ? false : enabled,
    queryKey,
    queryFn: () => {
      return rootId
        ? client[model][endPoint](rootId, cleanId, params)
        : client[model][endPoint](cleanId, params);
    },
    retry: (failureCount, error) => {
      return error?.response?.status !== 404 || failureCount > 2;
    },
  });

  const removeQuery = () => {
    queryClient
      .cancelQueries({
        queryKey: queryKey,
      })
      .then(() => {
        queryClient.removeQueries({
          queryKey: queryKey,
        });
      });
  };

  return {
    ...queryResult,
    data: data?.data?.data,
    meta: data?.data?.meta,
    removeQuery,
  };
};
